import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import type { RoadLandmark } from '@goodtime/shared';
import {
  landmarkAlias,
  roadStripLayout,
  TAG_HEIGHT,
  defaultArtShape,
  type ArtShape,
  type StripBox,
  type StripLayout,
} from '../../logic/road/landmarks';
import { artShape } from './artShape';
import { glowTexture, prefersReducedMotion, shadowTexture } from './glow';
import { COLOURS, displayStyle } from '../board/index';

const defaultShape = defaultArtShape();

interface Spot {
  root: Container;
  glow: Sprite;
  art: Sprite;
  /** The art's resting scale, for pulses. */
  scale: number;
}

/**
 * The road under the SPIN row: the slot's landmarks standing in a row on
 * the grass, each on its own soft shadow, painted whole once restored and
 * in ruins until then. The next
 * one to restore carries its price on a small plaque, filling as coins come
 * in, then RESTORE (and a glow) once the player can afford it. A tap on a
 * ruin calls {@link onTap}.
 */
export class LandmarkRoad extends Container {
  /** A ruin was tapped. */
  onTap: ((index: number) => void) | null = null;

  private readonly spotsLayer = new Container();
  private readonly tag = new Container();
  private readonly tagPlate = new Graphics();
  private readonly tagCoin = new Sprite();
  private readonly tagText: Text;
  private spots: Spot[] = [];
  private landmarks: readonly RoadLandmark[] = [];
  private box: StripBox | null = null;
  private coins = 0;
  private enabled = true;
  /** Lifted off the road while its restore plays. */
  private away: number | null = null;
  private clock = 0;
  private landing: { index: number; t: number } | null = null;
  private readonly reduced = prefersReducedMotion();

  constructor(private readonly texture: (alias: string) => Texture | null) {
    super();
    this.tagText = new Text({ text: '', style: displayStyle(14) });
    this.tagText.anchor.set(0, 0.5);
    this.tagCoin.anchor.set(0.5);
    this.tag.addChild(this.tagPlate, this.tagCoin, this.tagText);
    this.addChild(this.spotsLayer);
  }

  /** The landmarks to show (from the road state). */
  setLandmarks(landmarks: readonly RoadLandmark[]): void {
    this.landmarks = landmarks;
    this.rebuild();
  }

  /** The player's coins, for the next landmark's price tag. */
  setCoins(coins: number): void {
    if (coins === this.coins) return;
    this.coins = coins;
    this.drawTag();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    for (const s of this.spots) s.root.cursor = enabled ? 'pointer' : 'default';
  }

  /** Hide a landmark (it's away being restored), or show them all. */
  setAway(index: number | null): void {
    const was = this.away;
    this.away = index;
    for (const [i, s] of this.spots.entries()) s.root.visible = i !== index;
    if (index === null && was !== null) this.landing = { index: was, t: 0 };
    this.drawTag();
  }

  layout(box: StripBox | null): void {
    this.box = box;
    this.visible = box !== null && this.landmarks.length > 0;
    this.rebuild();
  }

  /**
   * Where landmark `index` stands, in the parent's coordinates: its
   * painting's square. `restored` asks where it stands in that state (the
   * restore moment flies home to its restored painting).
   */
  spot(index: number, restored?: boolean): { x: number; y: number; size: number } | null {
    const box = this.box;
    const l = this.landmarks[index];
    if (!box || !l) return null;
    const placed = this.layoutFor(index, restored ?? l.restored).landmarks[index];
    if (!placed) return null;
    return {
      x: box.left + placed.x - placed.size / 2,
      y: box.top + placed.y - placed.size / 2,
      size: placed.size,
    };
  }

  /** The painting landmark `l` shows in a state, and its shape. */
  private art(l: RoadLandmark, restored: boolean): { texture: Texture | null; shape: ArtShape } {
    const texture =
      this.texture(landmarkAlias(l.id, restored)) ?? this.texture(landmarkAlias(l.id, true));
    return { texture, shape: texture ? artShape(texture) : defaultShape };
  }

  /**
   * The row as it stands, or with landmark `index` shown `restored`. Sized
   * to fit every landmark's ruined and restored paintings, so it holds
   * still as they're restored.
   */
  private layoutFor(index = -1, restored = false): StripLayout {
    const box = this.box!;
    const shown = this.landmarks.map(
      (l, i) => this.art(l, i === index ? restored : l.restored).shape,
    );
    const fit = this.landmarks.flatMap((l) => [this.art(l, false).shape, this.art(l, true).shape]);
    return roadStripLayout(box.width, box.height, shown, fit);
  }

  private next(): number | null {
    const i = this.landmarks.findIndex((l) => !l.restored);
    return i < 0 ? null : i;
  }

  private rebuild(): void {
    this.spotsLayer
      .removeChildren()
      .forEach((c) => c !== this.tag && c.destroy({ children: true }));
    this.spots = [];
    const box = this.box;
    if (!box || this.landmarks.length === 0) return;
    this.position.set(box.left, box.top);
    const layout = this.layoutFor();

    const next = this.next();
    this.landmarks.forEach((l, i) => {
      const p = layout.landmarks[i]!;
      const root = new Container();
      root.position.set(p.x, p.y);
      // A soft shadow under its base sits it on the grass.
      const shadow = new Sprite(shadowTexture());
      shadow.anchor.set(0.5);
      shadow.width = p.baseWidth * 1.22;
      shadow.height = Math.max(8, p.baseWidth * 0.26);
      // Mostly under the base, peeking out below and to the sides.
      shadow.position.set(p.baseX - p.x, layout.baseline - p.y - shadow.height * 0.08);
      const glow = new Sprite(glowTexture());
      glow.anchor.set(0.5);
      glow.tint = 0xffd66e;
      glow.blendMode = 'add';
      glow.width = glow.height = p.size * 1.4;
      glow.visible = false;
      const { texture } = this.art(l, l.restored);
      const art = new Sprite(texture ?? Texture.EMPTY);
      art.anchor.set(0.5);
      const scale = texture ? p.scale : 1;
      art.scale.set(scale);
      // Ruins further on sit a touch duller than the next one.
      if (!l.restored && i !== next) art.tint = 0xdcd6cc;
      root.addChild(shadow, glow, art);
      root.visible = i !== this.away;
      // Ruins open their restore card; once the whole road is restored,
      // any landmark opens the chapter's card again.
      if (!l.restored || next === null) {
        // Taps land on its painting, from its top to its feet.
        const pad = 4;
        const hit = {
          left: p.left - p.x - pad,
          right: p.right - p.x + pad,
          top: p.top - p.y - pad,
          bottom: layout.baseline - p.y + pad,
        };
        root.eventMode = 'static';
        root.cursor = this.enabled ? 'pointer' : 'default';
        root.hitArea = {
          contains: (x: number, y: number) =>
            x >= hit.left && x <= hit.right && y >= hit.top && y <= hit.bottom,
        };
        root.on('pointertap', () => {
          if (this.enabled) this.onTap?.(i);
        });
      }
      this.spotsLayer.addChild(root);
      this.spots.push({ root, glow, art, scale });
    });
    this.spotsLayer.addChild(this.tag);
    this.drawTag();
  }

  /** The next landmark's price, hung from its feet. */
  private drawTag(): void {
    const next = this.next();
    const box = this.box;
    if (next === null || !box || next === this.away) {
      this.tag.visible = false;
      for (const s of this.spots) s.glow.visible = false;
      return;
    }
    const l = this.landmarks[next]!;
    const layout = this.layoutFor();
    const p = layout.landmarks[next]!;
    const affordable = this.coins >= l.cost;
    this.tagText.text = affordable ? 'RESTORE' : l.cost.toLocaleString('en-GB');
    // Dark on the gold plate once it's affordable; gold on wood until then.
    this.tagText.style = affordable
      ? { ...displayStyle(14, 0x3a1a00), stroke: undefined, dropShadow: undefined }
      : displayStyle(14);
    const coin = this.texture('shared:icon:coin');
    this.tagCoin.texture = coin ?? Texture.EMPTY;
    this.tagCoin.width = this.tagCoin.height = 17;
    const w = Math.round(4 + 17 + 4 + this.tagText.width + 8);
    const h = TAG_HEIGHT;
    const fill = affordable ? 1 : Math.max(0, Math.min(1, this.coins / l.cost));
    this.tagPlate
      .clear()
      .roundRect(-w / 2, 2, w, h, 8)
      .fill({ color: 0x000000, alpha: 0.35 })
      .roundRect(-w / 2, 0, w, h, 8)
      .fill(affordable ? 0xf0c050 : COLOURS.woodDark);
    if (!affordable && fill > 0)
      this.tagPlate.roundRect(-w / 2, 0, w * fill, h, 8).fill({ color: 0xd9a441, alpha: 0.55 });
    this.tagPlate.roundRect(-w / 2, 0, w, h, 8).stroke({ color: 0xd9a441, width: 1.5 });
    this.tagCoin.position.set(-w / 2 + 4 + 8.5, h / 2);
    this.tagText.position.set(-w / 2 + 4 + 17 + 4, h / 2);
    // Under its landmark's base, but kept on the road's box (the last one
    // sits near the screen's edge).
    const x = Math.max(w / 2 + 2, Math.min(box.width - w / 2 - 2, p.baseX));
    this.tag.position.set(x, layout.baseline - h * 0.25);
    this.tag.visible = true;
    for (const [i, s] of this.spots.entries()) s.glow.visible = i === next && affordable;
  }

  tick(deltaMs: number): void {
    if (!this.visible) return;
    this.clock += deltaMs;
    // The affordable landmark breathes a warm glow.
    const pulse = this.reduced ? 0.7 : 0.55 + 0.35 * Math.sin((this.clock / 1600) * Math.PI * 2);
    for (const s of this.spots) if (s.glow.visible) s.glow.alpha = pulse;
    // A landmark set back down restored pops and glows once.
    if (this.landing) {
      this.landing.t += deltaMs;
      const s = this.spots[this.landing.index];
      const k = Math.min(1, this.landing.t / 700);
      if (s) {
        s.art.scale.set(s.scale * (1 + 0.12 * (1 - k)));
        s.glow.visible = k < 1 || s.glow.visible;
        if (k < 1) s.glow.alpha = 1 - k;
      }
      if (k >= 1) {
        this.landing = null;
        this.drawTag();
      }
    }
  }
}
